<script setup lang="ts">
import {ref} from "vue";
import {Car, ClipboardPlus, Dices, Flower2, Landmark, Layers, PhoneCall, Pill, ShoppingBag} from "lucide-vue-next";

import '@/assets/styles/service.css';

interface Service {
  name: string;
  details: string;
  icon: any; // Icon Component
}

const selected = ref<Service | null>(null);

const services: Service[] = [
  {
    name: "Häusliche Krankenpflege",
    details:
        "Leistungen nach § 37.1 SGB V (Krankenhausvermeidungspflege) sowie § 37.2 SGB V (Behandlungspflege), Medikamentengabe, Wundversorgung, Injektionen (z. B. Insulin), Blutdruck- und Blutzuckerkontrolle, Kompressionsverbände, Versorgung nach Krankenhausaufenthalt, Krankenhausvermeidungspfelege",
    icon: Pill,
  },
  {
    name: "Grundpflege",
    details:
        "Leistungen nach § 36 SGB XI, Unterstützung bei der Körperpflege, Hilfe beim An- und Auskleiden, Mobilisation, Hilfe bei der Nahrungsaufnahme, Unterstützung bei der Toilettengang, Positionswechsel",
    icon: Layers,
  },
  {
    name: "Betreuung & Alltagsbegleitung",
    details:
        "Leistungen nach § 45b SGB XI, Stundenweise Betreuung, Aktivierende Gespräche, Begleitung zu Arztterminen, Spaziergänge, Unterstützung im Alltag",
    icon: Dices,
  },
  {
    name: "Hauswirtschaftliche Versorgung",
    details:
        "Reinigung der Wohnung, Wäschepflege, Einkaufen, Zubereitung von Mahlzeiten, Unterstützung im Haushalt",
    icon: ShoppingBag,
  },
  {
    name: "Beratung & Pflegegrad",
    details:
        "Pflegeberatung nach §37 SGB XI, Unterstützung bei Anträgen, Hilfe bei Einstufung in Pflegegrade, Beratung für Angehörige",
    icon: ClipboardPlus,
  },
  {
    name: "Verhinderungspflege",
    details:
        "Leistungen nach § 39 SGB XI, Vertretung pflegender Angehöriger, Kurzzeitige Entlastung im Alltag, Flexible Einsatzzeiten",
    icon: Car,
  },
  {
    name: "Individuelle Zusatzleistungen",
    details:
        "24-Stunden Rufbereitschaft, Individuelle Pflegekonzepte",
    icon: PhoneCall,
  },
  {
    name: "Gartenpflege",
    details:
        "Rasenpflege, Beetgestaltung, Heckenschnitt, Laubbeseitigung, Winterdienst",
    icon: Flower2,
  },
];
const showPopup = (service: Service) => {
  selected.value = service;
};

const closePopup = () => {
  selected.value = null;
};

// Hilfsfunktion für Liste im Template
const detailsList = (details: string) =>
    details.split(",").map(d => d.trim()).filter(d => d.length > 0);

</script>

<template>
  <Toast/>
  <div class="services">

    <section class="features">
      <div class="feature-wrapper">
        <h1>Dienstleistungen</h1>

        <div class="feature-grid">
          <div
              class="feature-card"
              v-for="service in services"
              :key="service.name"
              @click="showPopup(service)"
          >
            <component :is="service.icon" class="feature-icon"/>
            <h3>{{ service.name }}</h3>
          </div>
        </div>

        <!-- POPUP -->
        <div v-if="selected" class="popup-overlay" @click.self="closePopup">
          <div class="popup-box">
            <button class="close-btn-service" @click="closePopup">×</button>
            <component :is="selected.icon" class="popup-icon"/>
            <h2>{{ selected.name }}</h2>

            <ul class="popup-list">
              <li v-for="detail in detailsList(selected.details)" :key="detail">
                {{ detail }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="care-costs">
      <div class="costs-card">

        <h1>Kosten & Beratung</h1>

        <p>
          Viele unserer Leistungen werden von der Pflegekasse oder der Krankenkasse
          übernommen. Welche Unterstützung möglich ist, hängt vom Pflegegrad und der
          individuellen Situation ab.
        </p>

        <div class="feature-grid">

          <div class="feature-card">
            <div class="feature-icon">
              <ClipboardPlus/>

            </div>
            <h3>Pflegeversicherung</h3>
            <p>Leistungen nach Pflegegrad 1–5</p>
            <p>Verhinderungspflege</p>
            <p>Entlastungsbetrag</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <Pill/>
            </div>
            <h3>Häusliche Krankenpflege</h3>
            <p>Über ärztliche Verordnung (§ 37 SGB V)</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <Landmark/>
            </div>
            <h3>Weitere Kostenträger</h3>
            <p>Sozialhilfeträger</p>
            <p>Landeswohlfahrtsverband (LWV)</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <Layers/>
            </div>
            <h3>Individuelle Zusatzleistungen</h3>
            <p>Nach persönlichem Bedarf</p>
          </div>

        </div>

        <p class="cost-hint">
          Gerne beraten wir Sie persönlich und unterstützen Sie bei Anträgen sowie
          bei der Einstufung in einen Pflegegrad.
        </p>

        <a href="/contact" class="consult-button">
          Kostenlose Beratung anfragen
        </a>

      </div>
    </section>
  </div>
</template>

<style scoped>

</style>
